import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router';
import {
    RequestMethod,
    RequestStatus,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import {
    Add,
    AutoAwesomeMotion,
    CloudDownload,
    Edit,
    PictureAsPdf,
    StopCircle,
} from '@mui/icons-material';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import {
    Button,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { SeparationBar } from '../layout/components/SeparationBar.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { EmptyResults } from '../shared-mui-components/ListMessages.tsx';
import {
    OperatorProductStatus,
    OperatorProductStatusIcon,
} from '../products/OperatorProductState.tsx';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import { useState } from 'react';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { DateTime } from 'luxon';
import { LuxonDateStringFormats } from 'dg-web-shared/lib/LuxonDateStringFormats.ts';
import {
    isEventEditable,
    OperatorEventStatus,
} from './OperatorOffstreetEvents.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';

interface OffstreetEvent {
    eventId: string;
    eventName: string;
    stacks: OffstreetEventCardStack[];
    status: OperatorEventStatus;
    hasActiveProductTemplate: boolean;
}

interface OffstreetEventCardStack {
    stackId: string;
    stackState: OperatorProductStatus;
    stackName: string;
    count: number;
    terminatedAt: string | null;
}

export function OperatorOffstreetEventCards() {
    const { operatorExternalId, currentLogin } = useOperatorContext();
    const { eventId } = useParams();

    const [eventRequestState, refetchEvent] = useServerFetch<
        OffstreetEvent,
        { eventId: string }
    >(
        ({ eventId }) => ({
            url: `/ui-api/operator-account/offstreet-events/${eventId}`,
        }),
        eventId ? { eventId } : null,
    );
    const navigate = useNavigate();
    const { language } = useLanguage();

    return (
        <MainContent>
            <OperatorAsyncLoadedSection
                requestState={eventRequestState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={event => (
                    <>
                        <HeaderWithButtons
                            icon={AutoAwesomeMotion}
                            title={
                                <Localized
                                    de={`${event.eventName} – Kartenstapel`}
                                    fr={`${event.eventName} – Set de cartes`}
                                    it={`${event.eventName} – Set di carte`}
                                    en={`${event.eventName} – Cards stacks`}
                                />
                            }
                            headerButtons={
                                <Stack
                                    direction="row"
                                    spacing={3}
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    {currentLogin.permissions.eventsWrite && (
                                        <Tooltip
                                            title={
                                                !isEventEditable(
                                                    event.status,
                                                    event.hasActiveProductTemplate,
                                                ) ? (
                                                    <Localized
                                                        de="Das Event ist in der Vergangenheit oder der Produkttype ist nicht länger aktiv"
                                                        fr="L’événement est passé ou le type de produit n’est plus actif"
                                                        it="L'evento è nel passato o il tipo di prodotto non è più attivo"
                                                        en="The event is in the past or the product type is no longer active"
                                                    />
                                                ) : undefined
                                            }
                                            placement="bottom"
                                        >
                                            <Button
                                                variant="contained"
                                                startIcon={<Add />}
                                                color="primary"
                                                disabled={
                                                    !isEventEditable(
                                                        event.status,
                                                        event.hasActiveProductTemplate,
                                                    )
                                                }
                                                onClick={() =>
                                                    navigate('stack/add')
                                                }
                                            >
                                                <Localized
                                                    de="Stapel generieren"
                                                    fr="Générer set"
                                                    it="Genera set"
                                                    en="Generate stack"
                                                />
                                            </Button>
                                        </Tooltip>
                                    )}
                                    <Tooltip
                                        title={
                                            event.status !==
                                            OperatorEventStatus.EXPIRED ? (
                                                <Localized
                                                    de="Der Nutzungsbericht ist erst verfügbar, sobald die Veranstaltung vorbei ist."
                                                    fr="Le rapport d'usage n'est disponible qu'une fois la manifestation terminée."
                                                    it="Il resoconto d'utilizzo sarà disponibile solo al termine dell'evento."
                                                    en="The usage report is only available once the event is over."
                                                />
                                            ) : undefined
                                        }
                                        placement="bottom"
                                    >
                                        <span>
                                            <Button
                                                variant="outlined"
                                                startIcon={<CloudDownload />}
                                                color="primary"
                                                disabled={
                                                    event.status !==
                                                        OperatorEventStatus.EXPIRED ||
                                                    event.stacks.length < 1
                                                }
                                                href={urlWithOperatorExternalId(
                                                    `/ui-api/operator-account/offstreet-events/${eventId}/usage-report?lang=${language}`,
                                                    operatorExternalId,
                                                )}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <Localized
                                                    de="Nutzungsbericht"
                                                    fr="Rapport d'usage"
                                                    it="Resoconto d'utilizzo"
                                                    en="Usage report"
                                                />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            navigate('../offstreet-events')
                                        }
                                    >
                                        <Localized
                                            de="Schliessen"
                                            fr="Fermer"
                                            it="Chiudi"
                                            en="Close"
                                        />
                                    </Button>
                                </Stack>
                            }
                        />
                        <SeparationBar />
                        <OperatorOffstreetEventStackTable
                            eventId={event.eventId}
                            stacks={event.stacks}
                            refetchEventStacks={refetchEvent}
                        />
                        <OffstreetEventCardsOutlet
                            context={{
                                refetchEvent,
                                event,
                            }}
                        />
                    </>
                )}
            />
        </MainContent>
    );
}

function OperatorOffstreetEventStackTable({
    eventId,
    stacks,
    refetchEventStacks,
}: {
    eventId: string;
    stacks: OffstreetEventCardStack[];
    refetchEventStacks: () => void;
}) {
    if (stacks.length === 0) {
        return <EmptyResults hasFilter={false} />;
    }
    const hasTerminatedStack = stacks.some(stack => stack.terminatedAt != null);
    return (
        <TableContainer
            key={`Stack list container has restrictions ${hasTerminatedStack}`}
        >
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Localized
                                de="Status"
                                fr="État"
                                it="Stato"
                                en="State"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Stapel"
                                fr="Set"
                                it="Set"
                                en="Stack"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Anzahl Karten"
                                fr="Quantité de cartes"
                                it="Quantità carte"
                                en="Cards quantity"
                            />
                        </TableCell>
                        {hasTerminatedStack && (
                            <TableCell>
                                <Localized
                                    de="Einschränkung"
                                    fr="Restriction"
                                    it="Limitazione"
                                    en="Restriction"
                                />
                            </TableCell>
                        )}
                        <TableCell>
                            <Localized
                                de="Aktionen"
                                fr="Actions"
                                it="Azioni"
                                en="Actions"
                            />
                        </TableCell>
                        <TableCell>PDF</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {stacks.map(stack => (
                        <EventStackRow
                            key={stack.stackId}
                            eventId={eventId}
                            stack={stack}
                            hasTerminatedStack={hasTerminatedStack}
                            refetchEventStacks={refetchEventStacks}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function EventStackRow({
    eventId,
    stack,
    hasTerminatedStack,
    refetchEventStacks,
}: {
    eventId: string;
    stack: OffstreetEventCardStack;
    hasTerminatedStack: boolean;
    refetchEventStacks: () => void;
}) {
    const { operatorExternalId, currentLogin } = useOperatorContext();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [terminateModalOpen, setTerminateModalOpen] = useState(false);
    const terminatedAtString = stack.terminatedAt
        ? DateTime.fromISO(stack.terminatedAt).toFormat(
              LuxonDateStringFormats.DATE_HOUR_MINUTE,
          )
        : null;
    return (
        <TableRow>
            <TableCell>
                <OperatorProductStatusIcon status={stack.stackState} />
            </TableCell>
            <TableCell>{stack.stackName}</TableCell>
            <TableCell>{stack.count}</TableCell>
            {hasTerminatedStack && (
                <TableCell>
                    {terminatedAtString ? (
                        <Localized
                            de={`Nur bis ${terminatedAtString}`}
                            fr={`Jusqu'au ${terminatedAtString}`}
                            it={`Solo fino al ${terminatedAtString}`}
                            en={`Only until ${terminatedAtString}`}
                        />
                    ) : (
                        EmDash
                    )}
                </TableCell>
            )}
            <TableCell>
                <Stack
                    direction="row"
                    sx={{
                        display: 'flex',
                        flexShrink: 1,
                    }}
                >
                    {currentLogin.permissions.eventsWrite &&
                    [
                        OperatorProductStatus.FUTURE,
                        OperatorProductStatus.ACTIVE,
                    ].some(s => s == stack.stackState) ? (
                        <Tooltip
                            title={
                                <Localized
                                    de="Bearbeiten"
                                    fr="Modifier"
                                    it="Modifica"
                                    en="Edit"
                                />
                            }
                            placement="right"
                        >
                            <IconButton
                                color="primary"
                                onClick={() =>
                                    navigate(`stack/${stack.stackId}/edit`)
                                }
                            >
                                <Edit />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        EmDash
                    )}
                    {stack.stackState == OperatorProductStatus.FUTURE &&
                        currentLogin.permissions.eventsWrite && (
                            <Tooltip
                                title={
                                    <Localized
                                        de="Löschen"
                                        fr="Supprimer"
                                        it="Elimina"
                                        en="Delete"
                                    />
                                }
                                placement="right"
                            >
                                <IconButton
                                    color="primary"
                                    onClick={() => setDeleteModalOpen(true)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    {stack.stackState == OperatorProductStatus.ACTIVE &&
                        currentLogin.permissions.eventsWrite && (
                            <Tooltip
                                title={
                                    <Localized
                                        de="Beenden"
                                        fr="Terminer"
                                        it="Termina"
                                        en="Terminate"
                                    />
                                }
                                placement="right"
                            >
                                <IconButton
                                    color="primary"
                                    onClick={() => setTerminateModalOpen(true)}
                                >
                                    <StopCircle />
                                </IconButton>
                            </Tooltip>
                        )}
                </Stack>
            </TableCell>
            <TableCell>
                {[
                    OperatorProductStatus.FUTURE,
                    OperatorProductStatus.ACTIVE,
                ].some(s => s == stack.stackState) ? (
                    <a
                        target="_blank"
                        href={urlWithOperatorExternalId(
                            `/ui-api/operator-account/offstreet-events/${eventId}/stack/${stack.stackId}/pdf?language=${language}`,
                            operatorExternalId,
                        )}
                        rel="noreferrer"
                    >
                        <PictureAsPdf
                            sx={{
                                color: theme => theme.palette.primary.main,
                                '&:hover': {
                                    color: theme => theme.palette.blue.electric,
                                },
                            }}
                        />
                    </a>
                ) : (
                    EmDash
                )}
            </TableCell>
            <TerminationModal
                open={terminateModalOpen}
                closeModal={() => setTerminateModalOpen(false)}
                refetchEventStacks={refetchEventStacks}
                eventId={eventId}
                stack={stack}
            />
            <DeleteStackModal
                open={deleteModalOpen}
                closeModal={() => setDeleteModalOpen(false)}
                refetchEventStacks={refetchEventStacks}
                eventId={eventId}
                stack={stack}
            />
        </TableRow>
    );
}

function DeleteStackModal({
    open,
    closeModal,
    refetchEventStacks,
    eventId,
    stack,
}: {
    open: boolean;
    closeModal: () => void;
    refetchEventStacks: () => void;
    eventId: string;
    stack: OffstreetEventCardStack;
}) {
    const [deleteStackState, deleteStack] = useServerWrite<null, null, null>(
        () => ({
            url: `/ui-api/operator-account/offstreet-events/${eventId}/stack/${stack.stackId}/delete`,
            method: RequestMethod.PUT,
        }),
    );
    useServerSuccessEffect(deleteStackState, () => {
        refetchEventStacks();
        closeModal();
    });

    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Stapel löschen"
                    fr="Supprimer le set"
                    it="Elimina set"
                    en="Detete stack"
                />
            }
            confirmLabel={
                <Localized
                    de="Löschen"
                    fr="Supprimer"
                    it="Elimina"
                    en="Delete"
                />
            }
            onConfirm={() => deleteStack()}
            confirmDisabled={deleteStackState.status === RequestStatus.PENDING}
            onCancel={() => closeModal()}
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                <BackendRequestErrorMessage requestState={deleteStackState} />

                <p>
                    <Localized
                        de="Alle im Stapel enthaltenen Veranstaltungskarten werden annulliert und sind dann nicht mehr gültig."
                        fr="Toutes les cartes manifestation contenues dans le set seront annulées et ne seront plus valables."
                        it="Tutte le carte evento contenute nel set verranno annullate e non saranno più valide."
                        en="All event cards contained in the stack will be cancelled and will no longer be valid."
                    />
                </p>
                <p>
                    <Localized
                        de="Möchen Sie den Stapel wirklich löschen?"
                        fr="Voulez-vous vraiment supprimer le set?"
                        it="Vuole veramente eliminare il set?"
                        en="Would you like to delete the stack?"
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

function TerminationModal({
    open,
    closeModal,
    refetchEventStacks,
    eventId,
    stack,
}: {
    open: boolean;
    closeModal: () => void;
    refetchEventStacks: () => void;
    eventId: string;
    stack: OffstreetEventCardStack;
}) {
    const [terminateStackState, terminateStack] = useServerWrite<
        null,
        null,
        null
    >(() => ({
        url: `/ui-api/operator-account/offstreet-events/${eventId}/stack/${stack.stackId}/terminate`,
        method: RequestMethod.PUT,
    }));
    useServerSuccessEffect(terminateStackState, () => {
        refetchEventStacks();
        closeModal();
    });

    return (
        <ConfirmationDialog
            open={open}
            title={
                <Localized
                    de="Stapel beenden"
                    fr="Terminer le set"
                    it="Termina il set"
                    en="Terminate the stack"
                />
            }
            confirmLabel={
                <Localized
                    de="Beenden"
                    fr="Terminer"
                    it="Termina"
                    en="Terminate"
                />
            }
            onConfirm={() => terminateStack()}
            confirmDisabled={
                terminateStackState.status === RequestStatus.PENDING
            }
            onCancel={() => closeModal()}
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <>
                <BackendRequestErrorMessage
                    requestState={terminateStackState}
                />

                <p>
                    <Localized
                        de="Die Gültigkeit aller im Stapel enthaltenen Veranstaltungskarten wird sofort beendet."
                        fr="La validité de toutes les cartes manifestation contenues dans le set serà immédiatement terminée."
                        it="La validità di tutte le carte evento contenute nel set verrà terminata immediatamente."
                        en="The validity of all event cards contained in the stack will be terminated immediately."
                    />
                </p>
                <p>
                    <Localized
                        de="Möchen Sie den Stapel wirklich beenden?"
                        fr="Voulez-vous vraiment terminer le set?"
                        it="Vuole veramente terminare il set?"
                        en="Would you like terminate the stack?"
                    />
                </p>
            </>
        </ConfirmationDialog>
    );
}

export function useOffstreetEventCardsOutlet() {
    return useOutletContext<OffstreetEventCardsOutletContext>();
}

function OffstreetEventCardsOutlet({
    context,
}: {
    context: OffstreetEventCardsOutletContext;
}) {
    return <Outlet context={context} />;
}

export interface OffstreetEventCardsOutletContext {
    refetchEvent: () => void;
    event: OffstreetEvent;
}
